import type { MacroBlockStatus, SessionStatus, StudyDayStatus } from "./models";

const dayTransitions: Readonly<Record<StudyDayStatus, readonly StudyDayStatus[]>> = {
  draft: ["issued", "rescheduled"],
  issued: ["acknowledged", "draft", "rescheduled"],
  acknowledged: ["in_progress", "rescheduled"],
  in_progress: ["awaiting_report", "rescheduled"],
  awaiting_report: ["submitted", "in_progress", "rescheduled"],
  submitted: ["oral_review", "revision_required", "accepted"],
  oral_review: ["revision_required", "accepted"],
  revision_required: ["submitted", "oral_review", "accepted"],
  accepted: ["archived"],
  archived: [],
  rescheduled: ["draft"],
};

const macroBlockTransitions: Readonly<Record<MacroBlockStatus, readonly MacroBlockStatus[]>> = {
  not_started: ["in_progress", "skipped"],
  in_progress: ["completed", "partial", "skipped"],
  completed: [],
  partial: ["in_progress", "completed"],
  skipped: ["not_started"],
};

const sessionTransitions: Readonly<Record<SessionStatus, readonly SessionStatus[]>> = {
  not_started: ["in_progress", "skipped"],
  in_progress: ["paused", "completed", "partial", "revisit", "skipped"],
  paused: ["in_progress", "completed", "partial", "skipped"],
  completed: ["revisit"],
  partial: ["in_progress", "completed", "revisit"],
  skipped: ["not_started", "revisit"],
  revisit: ["in_progress", "completed", "partial"],
};

export class InvalidStateTransitionError extends Error {
  constructor(entity: string, from: string, to: string) {
    super(`${entity} 不允许从 ${from} 转为 ${to}`);
    this.name = "InvalidStateTransitionError";
  }
}

export function canTransitionStudyDay(from: StudyDayStatus, to: StudyDayStatus): boolean {
  return dayTransitions[from].includes(to);
}

export function assertStudyDayTransition(from: StudyDayStatus, to: StudyDayStatus): void {
  if (!canTransitionStudyDay(from, to)) throw new InvalidStateTransitionError("每日任务", from, to);
}

export function canTransitionMacroBlock(from: MacroBlockStatus, to: MacroBlockStatus): boolean {
  return macroBlockTransitions[from].includes(to);
}

export function assertMacroBlockTransition(from: MacroBlockStatus, to: MacroBlockStatus): void {
  if (!canTransitionMacroBlock(from, to)) throw new InvalidStateTransitionError("宏块", from, to);
}

export function canTransitionSession(from: SessionStatus, to: SessionStatus): boolean {
  return sessionTransitions[from].includes(to);
}

export function assertSessionTransition(from: SessionStatus, to: SessionStatus): void {
  if (!canTransitionSession(from, to)) throw new InvalidStateTransitionError("Session", from, to);
}

export function allowedStudyDayTransitions(status: StudyDayStatus): readonly StudyDayStatus[] {
  return dayTransitions[status];
}

export function allowedMacroBlockTransitions(status: MacroBlockStatus): readonly MacroBlockStatus[] {
  return macroBlockTransitions[status];
}

export function allowedSessionTransitions(status: SessionStatus): readonly SessionStatus[] {
  return sessionTransitions[status];
}
